import { Link } from "react-router-dom";
import { ArrowUpRight, Award, Code, Globe2, HeartHandshake } from "lucide-react";
import { COMPANY, STATS, TESTIMONIALS } from "@/data/site";

const TIMELINE = [
  { year: "2016", t: "The Beginning", d: "Jagjeet Singh starts building websites for small businesses — focused on craft and reliability." },
  { year: "2018", t: "First Web Apps", d: "Transitioned to building full-stack web applications using React, Node and PostgreSQL." },
  { year: "2020", t: "Mobile Expansion", d: "Launched mobile app practice with React Native — shipped apps in healthcare and retail." },
  { year: "2022", t: "Cloud & DevOps", d: "Built managed AWS infra, Docker-based CI/CD and 24/7 monitoring for enterprise clients." },
  { year: "2024", t: "Web3 & AI", d: "Added audited Solidity smart contracts and AI-driven automation to the service portfolio." },
  { year: "2026", t: "Today", d: "240+ clients across 4 continents, multi-disciplinary remote team, serving businesses worldwide." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-[#1f1f22] relative overflow-hidden">
        <div className="glow-spot" style={{ width: 600, height: 600, background: "#DC1F33", top: "-220px", left: "-160px" }} />
        <div className="container-x py-24 md:py-32 relative">
          <div className="label-mono mb-6">// About / The Studio</div>
          <h1 className="font-display text-[clamp(40px,7vw,96px)] font-black uppercase tracking-tight leading-[0.92] text-white max-w-5xl">
            We engineer<br /><span className="text-[#DC1F33]">long-term partnerships,</span><br /><span className="text-zinc-500">not just websites.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl">
            Jeet IT Services is an independent engineering studio founded by Jagjeet Singh in 2016. We help businesses worldwide move from spreadsheets and fragile tools to scalable, secure, modern digital systems.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-[#1f1f22]">
            {STATS.map((s) => (
              <div key={s.label} className="grid-cell">
                <div className="font-display text-5xl md:text-6xl font-black text-white tracking-tight">{s.value}</div>
                <div className="label-mono mt-2">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-16">
          <div>
            <div className="label-mono mb-4">// Founder</div>
            <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95]">
              {COMPANY.founder}
            </h2>
            <p className="label-mono mt-3">Full-stack engineer · Founder · Working globally</p>

            <div className="mt-10 border border-[#27272A] bg-[#0E0E10] p-6">
              <p className="text-zinc-300 leading-relaxed text-[15px] italic">
                "I started Jeet IT because I was tired of seeing businesses get burned by agencies that shipped buggy, bloated software. Our entire bet is simple — quality engineering, transparent communication, and a 10-year horizon on every relationship."
              </p>
            </div>
          </div>

          <div>
            <div className="label-mono mb-4">// What we believe</div>
            <div className="grid sm:grid-cols-2 border-l border-t border-[#1f1f22]">
              {[
                { icon: Code, t: "Engineering > marketing", d: "We optimize for code quality, not pitch decks. Clients keep the source, the docs and the keys." },
                { icon: Globe2, t: "Global perspective", d: "We work across time zones, payment networks and compliance regimes — wherever your customers are." },
                { icon: HeartHandshake, t: "Real partnerships", d: "98% retention isn't luck — it's the byproduct of treating every client like our only client." },
                { icon: Award, t: "Boring is beautiful", d: "Predictable timelines, stable systems, clean handoffs. We'd rather be reliable than flashy." },
              ].map((x) => {
                const I = x.icon;
                return (
                  <div key={x.t} className="grid-cell">
                    <I className="text-[#DC1F33] mb-5" size={22} />
                    <div className="font-display text-lg font-bold text-white mb-2">{x.t}</div>
                    <p className="text-sm text-zinc-400 leading-relaxed">{x.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="label-mono mb-4">// Journey</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-16 max-w-3xl">
            A decade of <span className="text-[#DC1F33]">shipping software.</span>
          </h2>

          <div className="grid md:grid-cols-2 border-l border-t border-[#1f1f22]">
            {TIMELINE.map((p) => (
              <div key={p.year} className="grid-cell flex flex-col gap-4">
                <div className="flex items-baseline gap-6">
                  <div className="font-display text-5xl font-black text-[#DC1F33] leading-none">{p.year}</div>
                  <div className="font-display text-xl font-bold text-white tracking-tight">{p.t}</div>
                </div>
                <p className="text-zinc-400 text-[15px] leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// Voices</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-14">
            What clients say.
          </h2>
          <div className="grid md:grid-cols-3 border-l border-t border-[#1f1f22]">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="grid-cell">
                <div className="font-display text-3xl text-[#DC1F33] mb-4">&ldquo;</div>
                <p className="text-zinc-200 leading-relaxed mb-8 text-[15px]">{t.quote}</p>
                <div>
                  <div className="font-display font-bold text-white">{t.name}</div>
                  <div className="label-mono">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="border border-[#27272A] p-12 md:p-16 bg-[#0E0E10] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="label-mono mb-3">// Work with us</div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Have a project in mind?</h3>
              <p className="text-zinc-400 mt-3 max-w-xl">We respond to every inquiry within one business day — usually faster.</p>
            </div>
            <Link to="/contact" data-testid="about-final-cta" className="btn-primary">
              Start a Conversation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
